import { gql } from './graphql';
import { isDockUnavailableForMovement } from '../utils/dock';

export interface DockDoor {
  dockUuid: string;
  dockName: string;
  zoneUuid: string;
  zoneName?: string;
  dockStatus?: string;
  trailer_uuid?: string;
  trailer_name?: string;
  carrier_name?: string;
  isLocked?: boolean;
  hasMoveFromTask?: boolean;
  configuration?: any;
}

export interface YardSpot {
  yard_spot_uuid: string;
  yard_spot_name: string;
  facility_zone_uuid: string;
  zoneName?: string;
  yard_spot_status?: string;
  /** Set when a trailer is currently in this spot. */
  trailer_name?: string;
  carrier_name?: string;
  isLocked?: boolean;
  configuration?: any;
}

export interface FacilityZone {
  facility_zone_uuid: string;
  facility_zone_name: string;
  zone_type?: string;
  configuration?: {
    color?: string;
  };
  trailers?: any[];
}

export interface AvailableLocations {
  dockDoors: DockDoor[];
  yardSpots: YardSpot[];
  facilityZones: FacilityZone[];
}

const AVAILABLE_LOCATIONS = `
  query GetAvailableLocationsAndLocks($facilityId: ID!) {
    warehouse(facilityId: $facilityId) {
      facilityId
      warehouseZones {
        zoneUuid
        zoneName
        docks {
          dockUuid
          dockName
          dockStatus
          trailer_uuid
          trailer_name
          carrier_name
          configuration
        }
      }
    }
    yard(facility_id: $facilityId) {
      facility_zones {
        facility_zone_uuid
        facility_zone_name
        zone_type
        configuration
        yard_spots {
          yard_spot_uuid
          yard_spot_name
          yard_spot_status
          trailer_name
          carrier_name
          configuration
        }
      }
    }
    facilityLocks(facilityId: $facilityId) {
      resource_uuid
      model_name
      resource {
        ... on YardSpotObject {
          uuid
        }
        ... on DockObject {
          uuid
        }
        ... on Trailer {
          uuid
        }
      }
    }
  }
`;

/**
 * The docks, yard spots and facility zones a trailer can be placed at, as the
 * Add Trailer modal lists them: each dock and spot with whether it is taken or
 * locked, sorted by its configured order and then by name.
 */
export async function loadAvailableLocations(
  facilityId: string
): Promise<AvailableLocations> {
  const data = await gql<any>(AVAILABLE_LOCATIONS, { facilityId });
  const locked = lockedResources(data.facilityLocks || []);

  const dockDoors: DockDoor[] = [];
  for (const zone of data.warehouse?.warehouseZones ?? []) {
    for (const dock of zone.docks ?? []) {
      if (isDockUnavailableForMovement(dock.configuration)) continue;

      // A locked door is unavailable. A door with a trailer is unavailable
      // unless that trailer is locked — being moved out — in which case it will
      // free up, and has a move-from task.
      let isLocked = false;
      let hasMoveFromTask = false;
      if (locked.docks.has(dock.dockUuid)) {
        isLocked = true;
      } else if (dock.trailer_uuid) {
        if (locked.trailers.has(dock.trailer_uuid)) hasMoveFromTask = true;
        else isLocked = true;
      }

      dockDoors.push({
        dockUuid: dock.dockUuid,
        dockName: dock.dockName,
        zoneUuid: zone.zoneUuid,
        zoneName: zone.zoneName,
        dockStatus: dock.dockStatus,
        trailer_uuid: dock.trailer_uuid,
        trailer_name: dock.trailer_name,
        carrier_name: dock.carrier_name,
        isLocked,
        hasMoveFromTask,
        configuration: dock.configuration
      });
    }
  }

  const yardSpots: YardSpot[] = [];
  const facilityZones: FacilityZone[] = [];
  for (const zone of data.yard?.facility_zones ?? []) {
    facilityZones.push({
      facility_zone_uuid: zone.facility_zone_uuid,
      facility_zone_name: zone.facility_zone_name,
      zone_type: zone.zone_type,
      configuration: zone.configuration,
      trailers: zone.trailers || []
    });

    for (const spot of zone.yard_spots ?? []) {
      yardSpots.push({
        yard_spot_uuid: spot.yard_spot_uuid,
        yard_spot_name: spot.yard_spot_name,
        facility_zone_uuid: zone.facility_zone_uuid,
        zoneName: zone.facility_zone_name,
        yard_spot_status: spot.yard_spot_status,
        trailer_name: spot.trailer_name,
        carrier_name: spot.carrier_name,
        isLocked: locked.yardSpots.has(spot.yard_spot_uuid),
        configuration: spot.configuration
      });
    }
  }

  return {
    dockDoors: sortByOrderThenName(dockDoors, (d) => d.dockName),
    yardSpots: sortByOrderThenName(yardSpots, (s) => s.yard_spot_name),
    facilityZones
  };
}

interface LockedResources {
  docks: Set<string>;
  yardSpots: Set<string>;
  trailers: Set<string>;
}

function lockedResources(locks: any[]): LockedResources {
  const locked: LockedResources = {
    docks: new Set<string>(),
    yardSpots: new Set<string>(),
    trailers: new Set<string>()
  };

  for (const lock of locks) {
    const uuid = lock.resource_uuid || lock.resource?.uuid;
    const kind = lockedKind(lock.model_name) ?? lockedKind(lock.resource?.__typename);
    if (uuid && kind) locked[kind].add(uuid);
  }

  return locked;
}

// What a lock is on, from its model name (or its resource's GraphQL type).
function lockedKind(name: string | undefined): keyof LockedResources | null {
  switch (name) {
    case 'Dock':
    case 'DockObject':
      return 'docks';
    case 'YardSpot':
    case 'YardSpotObject':
      return 'yardSpots';
    case 'Trailer':
      return 'trailers';
    default:
      return null;
  }
}

// By `configuration.order` where it is set (non-zero), those first; then by
// name, numerically when both names are numbers.
function sortByOrderThenName<T extends { configuration?: any }>(
  items: T[],
  nameOf: (item: T) => string | undefined
): T[] {
  const hasOrder = (item: T) => {
    const order = item.configuration?.order;
    return order !== null && order !== undefined && order !== 0;
  };

  return items.sort((a, b) => {
    if (hasOrder(a) && hasOrder(b)) return a.configuration.order - b.configuration.order;
    if (hasOrder(a)) return -1;
    if (hasOrder(b)) return 1;

    const nameA = nameOf(a) || '';
    const nameB = nameOf(b) || '';
    const numA = parseInt(nameA, 10);
    const numB = parseInt(nameB, 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
  });
}
